/**
 * Canonicalisation — the one place a string is made comparable to another string.
 *
 * Two different jobs live here and they must never be confused:
 *
 *  1. `normalizeBlockText()` produces `Block.text` (§2.2) — the string that is hashed, spoken,
 *     segmented and highlighted. It is *readable*: punctuation, case and diacritics all survive,
 *     because this string is what the reader displays and what the offsets in `Block.sentences`
 *     index into. It only removes what is invisible or non-semantic.
 *
 *  2. `normForKey()` produces a *matching* form for §3.6 re-import (`anchor`, `selfKey`). It is
 *     aggressive and unreadable: case, punctuation and diacritics are thrown away so that a Calibre
 *     re-export which turns `"` into a curly quote still matches the sentence it is the same
 *     sentence as. Nothing in the reading or narration path may use it.
 *
 * `shared/` is bundled by the client, so this file has no `node:` imports and does no I/O.
 */

/**
 * NFC, not NFKC, for display text.
 *
 * NFKC would fold ligatures (ﬁ → fi), superscripts (² → 2) and full-width forms — useful for
 * *matching*, destructive for *display*, and it would also rewrite a typographic ligature into two
 * characters after the offsets that point at it were computed. Compatibility folding happens in
 * `normForKey()`, where nothing indexes into the result.
 */
export const nfc = (s: string): string => s.normalize("NFC");

/**
 * The invisible characters are named by code point and the character classes are *built* from those
 * numbers, never typed as literals.
 *
 * A literal zero-width character inside a `/[...]/` is invisible in the editor, in a diff and in a
 * review: nobody can see it, nobody can see it removed, and a literal U+0000 makes the file itself
 * read as binary to `grep` and to CI. The code points below are the record of what we strip.
 */
const cp = (n: number) => String.fromCodePoint(n);
const range = (lo: number, hi: number) => `${cp(lo)}-${cp(hi)}`;
const charClass = (...members: string[]) => new RegExp(`[${members.join("")}]`, "gu");

/**
 * Invisible on the page, and otherwise present inside a sentence span, inside a hash, and inside the
 * text sent to the GPU.
 *
 *   U+00AD SOFT HYPHEN — publishers' EPUBs are full of them, they never render, and one inside a
 *                        word breaks `selfKey`, the TTS text and any search over the book.
 *   U+200B ZERO WIDTH SPACE
 *   U+200E/U+200F LEFT/RIGHT-TO-LEFT MARK
 *   U+2060 WORD JOINER, U+2061..U+2064 invisible maths operators
 *   U+FEFF ZERO WIDTH NO-BREAK SPACE (a BOM that survived a concatenation)
 *
 * Deliberately NOT here: U+200C ZWNJ and U+200D ZWJ. In Devanagari, Persian and Arabic they are
 * orthographic — removing them changes the word — and in emoji they are what holds a sequence
 * together. "Zero-width" describes how a character renders, not whether it means anything.
 */
const INVISIBLE = charClass(cp(0x00ad), cp(0x200b), cp(0x200e), cp(0x200f), range(0x2060, 0x2064), cp(0xfeff));

/**
 * Control characters that are not whitespace (`\t`, `\n`, `\r` are left to `collapseWhitespace`).
 * A stray U+0000 in a PDF text layer is not hypothetical, and it survives every later layer silently.
 */
const CONTROLS = charClass(range(0x0000, 0x0008), cp(0x000b), cp(0x000c), range(0x000e, 0x001f), cp(0x007f));

export const stripInvisible = (s: string): string => s.replace(INVISIBLE, "").replace(CONTROLS, "");

/**
 * All whitespace runs — including newlines, tabs, NBSP and the Unicode spaces — become one U+0020.
 *
 * A block is one line by construction (§2.2), so the newlines an EPUB or a PDF text layer carries
 * are layout, not content. NBSP collapses too: it is a rendering instruction, and keeping it would
 * mean two spellings of `Chapter 5` hash differently and generate two clips of the same words on the
 * same GPU.
 */
export const collapseWhitespace = (s: string): string => s.replace(/\s+/gu, " ").trim();

/** The canonical `Block.text`: NFC, no invisibles, one-space-separated, trimmed. */
export const normalizeBlockText = (raw: string): string => collapseWhitespace(stripInvisible(nfc(raw)));

/**
 * The matching form for §3.6. Compatibility-folded, unaccented, lowercased, punctuation-free.
 *
 * `toLowerCase()` and never `toLocaleLowerCase()`: under a Turkish locale the latter maps `I` to a
 * dotless `ı`, which would make a stored key depend on the environment of whichever box ran the
 * ingest. A key that differs between amd-server and cloud-2 fails to match the sentence it *is*, and
 * the realign ladder reports an orphan with nothing to point at.
 *
 * A punctuation-only sentence normalises to `""`. That is correct, and it is why `selfKey` is the
 * *second* rung of the ladder rather than the first — an empty key matches every other empty key.
 */
export const normForKey = (s: string): string =>
  stripInvisible(s)
    .normalize("NFKD")
    .replace(/\p{M}+/gu, "")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, " ")
    .trim();

/**
 * ISO 15924 codes, because `Block.lang` is BCP-47 and a script subtag is exactly this alphabet.
 * `Zyyy` (Common) means "no letters at all" — a rule, a row of digits, a block of punctuation.
 */
export type Script =
  | "Latn" | "Cyrl" | "Grek"
  | "Hani" | "Hira" | "Kana" | "Hang"
  | "Arab" | "Hebr" | "Deva" | "Thai"
  | "Zyyy";

/** Ordered by how likely we are to see it: detection stops at the first match for each letter. */
const SCRIPT_TESTS: readonly (readonly [Script, RegExp])[] = [
  ["Latn", /\p{Script=Latin}/u],
  ["Cyrl", /\p{Script=Cyrillic}/u],
  ["Grek", /\p{Script=Greek}/u],
  ["Hani", /\p{Script=Han}/u],
  ["Hira", /\p{Script=Hiragana}/u],
  ["Kana", /\p{Script=Katakana}/u],
  ["Hang", /\p{Script=Hangul}/u],
  ["Arab", /\p{Script=Arabic}/u],
  ["Hebr", /\p{Script=Hebrew}/u],
  ["Deva", /\p{Script=Devanagari}/u],
  ["Thai", /\p{Script=Thai}/u],
];

const LETTER = /\p{L}/u;

/** Enough letters to decide: a block whose first 400 letters are Han is a Han block. */
const SCRIPT_SAMPLE_LETTERS = 400;

/**
 * The dominant script of a string, by letter count over a bounded sample.
 *
 * This is what is stored as `blocks.script`, and it is what stops the segmenter guessing (§3.2). It
 * is a *majority* verdict, not a purity check: a Japanese paragraph quoting an English title is
 * still Japanese, and an English paragraph quoting one Greek word is still English.
 */
export function detectScript(text: string): Script {
  const counts = new Map<Script, number>();
  let seen = 0;

  for (const ch of text) {
    if (!LETTER.test(ch)) continue;
    for (const [script, test] of SCRIPT_TESTS) {
      if (!test.test(ch)) continue;
      counts.set(script, (counts.get(script) ?? 0) + 1);
      break;
    }
    if (++seen >= SCRIPT_SAMPLE_LETTERS) break;
  }

  let best: Script = "Zyyy";
  let bestCount = 0;
  for (const [script, count] of counts) {
    if (count > bestCount) {
      best = script;
      bestCount = count;
    }
  }
  return best;
}

/**
 * The scripts our sentence rules are actually true for.
 *
 * These three write sentences with `.` `!` `?` and separate words with spaces, which is every
 * assumption `segment()` makes. Han, Kana, Hangul, Arabic, Hebrew, Devanagari and Thai each break at
 * least one of them — a full stop that is not a full stop, a question mark that is not one, no
 * interword spaces — and §3.2 is explicit that we record the script and emit one span rather than
 * guess. A wrong guess is not a visible bug; it is a highlight that lights half a line while the
 * voice reads the whole one.
 *
 * `Zyyy` is included because a block with no letters (a rule, `* * *`, a row of numbers) has nothing
 * to get wrong, and calling it unsegmentable would hand the reader a span that never gets narrated.
 */
export const isSegmentableScript = (script: Script): boolean =>
  script === "Latn" || script === "Cyrl" || script === "Grek" || script === "Zyyy";
